// Lets an admin arrange the ledger list into the order they actually read it
// in — Transport before Food before Rent — instead of the alphabetical default.
//
// Dragging uses the browser's own HTML5 drag events rather than a library: the
// list is short, vertical and single-level, which is the one case native drag
// handles well without a dependency. The ▲▼ buttons aren't decoration — native
// drag is unreliable on touch and unusable from a keyboard, so they're the
// accessible path to the same result.

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { colors, spacing, fontSize, fontWeight, radius } from '../styles/theme';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function getAuthHeader() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface Category { id: number; name: string; display_order?: number }

interface Props {
  open: boolean;
  onClose: () => void;
  categories: Category[];
  /** Called after a successful save so the caller can refetch. */
  onSaved: () => void;
}

export default function ReorderLedgersModal({ open, onClose, categories, onSaved }: Props) {
  const [items, setItems] = useState<Category[]>([]);
  const [busy, setBusy] = useState(false);
  const dragFrom = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  // Reseed each time it opens so a cancelled edit never leaks into the next one.
  useEffect(() => { if (open) setItems(categories); }, [open, categories]);

  if (!open) return null;

  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length || from === to) return;
    setItems(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const dirty = items.some((c, i) => categories[i]?.id !== c.id);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(
        `${API}/api/financial/categories/order`,
        { ordered_ids: items.map(c => c.id) },
        { headers: getAuthHeader() }
      );
      toast.success('Ledger order saved');
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to save the order');
    } finally {
      setBusy(false);
    }
  };

  return (
    // data-ledger-modal: the workspace treats a click outside the table as a
    // click away from an open edit row. Without this marker, opening this modal
    // mid-edit would raise the unsaved-changes prompt behind it.
    <div style={overlay} data-ledger-modal onClick={() => !busy && onClose()}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.textPrimary }}>
            Reorder ledgers
          </div>
          <button onClick={onClose} style={closeBtn}>&times;</button>
        </div>

        <p style={{ fontSize: fontSize.sm, color: colors.textMuted, marginTop: spacing(1), marginBottom: 0 }}>
          Drag a ledger, or use the arrows. This sets the grouping order in the workspace
          and the order of every ledger dropdown.
        </p>

        <div style={list}>
          {items.map((cat, i) => (
            <div
              key={cat.id}
              draggable
              onDragStart={() => { dragFrom.current = i; }}
              onDragOver={e => { e.preventDefault(); setDragOver(i); }}
              onDragLeave={() => setDragOver(prev => (prev === i ? null : prev))}
              onDrop={e => {
                e.preventDefault();
                if (dragFrom.current !== null) move(dragFrom.current, i);
                dragFrom.current = null;
                setDragOver(null);
              }}
              onDragEnd={() => { dragFrom.current = null; setDragOver(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: spacing(2),
                padding: `${spacing(2)} ${spacing(2)}`,
                borderBottom: `1px solid ${colors.borderMuted}`,
                background: dragOver === i ? colors.primaryBg : colors.surface,
                borderTop: dragOver === i ? `2px solid ${colors.primary}` : '2px solid transparent',
                cursor: 'grab',
              }}
            >
              <GripVertical size={14} style={{ color: colors.textFaint, flexShrink: 0 }} />
              <span style={{ width: 22, fontSize: fontSize.xs, color: colors.textFaint, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                {i + 1}
              </span>
              <span style={{
                flex: 1, minWidth: 0, fontSize: fontSize.md, color: colors.textSecondary,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {cat.name}
              </span>
              <button onClick={() => move(i, i - 1)} disabled={i === 0} title="Move up"
                style={{ ...arrowBtn, opacity: i === 0 ? 0.3 : 1 }}>
                <ChevronUp size={14} />
              </button>
              <button onClick={() => move(i, i + 1)} disabled={i === items.length - 1} title="Move down"
                style={{ ...arrowBtn, opacity: i === items.length - 1 ? 0.3 : 1 }}>
                <ChevronDown size={14} />
              </button>
            </div>
          ))}
          {items.length === 0 && (
            <div style={{ padding: spacing(4), textAlign: 'center', color: colors.textFaint, fontSize: fontSize.sm }}>
              No ledgers yet.
            </div>
          )}
        </div>

        <div style={footer}>
          <button onClick={onClose} disabled={busy}
            style={{ ...btn, background: colors.surface, border: `1px solid ${colors.border}`, color: colors.textSecondary }}>
            Cancel
          </button>
          <button onClick={save} disabled={busy || !dirty}
            style={{ ...btn, background: colors.primary, border: 'none', color: '#fff', opacity: busy || !dirty ? 0.5 : 1 }}>
            {busy ? 'Saving…' : 'Save order'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 10001,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: spacing(4),
};
const card: React.CSSProperties = {
  background: colors.surface, padding: '1.5rem', borderRadius: radius.xl,
  width: '440px', maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 20px 40px rgba(0,0,0,0.25)', boxSizing: 'border-box',
};
const list: React.CSSProperties = {
  marginTop: spacing(3), border: `1px solid ${colors.borderMuted}`, borderRadius: radius.md,
  overflowY: 'auto', flex: '1 1 auto', minHeight: 0,
};
const footer: React.CSSProperties = {
  display: 'flex', gap: spacing(2), justifyContent: 'flex-end',
  marginTop: spacing(4), paddingTop: spacing(4), borderTop: `1px solid ${colors.borderMuted}`,
  flex: '0 0 auto',
};
const btn: React.CSSProperties = {
  padding: '0.55rem 1.1rem', borderRadius: radius.sm, fontSize: fontSize.md,
  fontWeight: fontWeight.semibold, cursor: 'pointer',
};
const arrowBtn: React.CSSProperties = {
  background: colors.surfaceMuted, border: `1px solid ${colors.border}`, borderRadius: radius.sm,
  cursor: 'pointer', color: colors.textSecondary, padding: '2px 4px',
  display: 'inline-flex', alignItems: 'center', flexShrink: 0,
};
const closeBtn: React.CSSProperties = {
  background: 'none', border: 'none', fontSize: '1.4rem',
  cursor: 'pointer', color: colors.textMuted, lineHeight: 1,
};
